/**
 * usgsEpqsFetch — USGS National Map Elevation Point Query Service
 * (EPQS) helper used as Stage 4 of the EAS cascade.
 *
 * Pack: `docs/prompt-packs/Elevation_Averaging_Sampling_Prompt.md`
 * (EAS-2 — USGS EPQS fetch helper).
 *
 * Why we need it
 * --------------
 * Google Maps Elevation API has near-global coverage but rural /
 * international polygons can return empty for vertices near coverage
 * edges. USGS EPQS is free, no-auth, and has US-CONUS coverage at
 * ≤3 m horizontal resolution — often higher fidelity than Google's
 * 90 m SRTM tiles. When Stages 1-3 (Google polygon-sample, in-polygon
 * grid, buffered-bbox grid) all return empty, we try a single USGS
 * EPQS query at the polygon centroid as the last-ditch fallback.
 *
 * Endpoint
 * --------
 * `https://epqs.nationalmap.gov/v1/json?x={lng}&y={lat}&units=Meters
 *  &wkid=4326&includeDate=false`
 *
 * The legacy `nationalmap.gov/epqs/pqs.php` endpoint listed in the EAS
 * pack draft was deprecated in 2023; we use the v1 JSON endpoint that
 * the existing `siteProfile.ts` synthetic provider already proves out
 * in production. Response shape:
 *
 * ```
 * { "value": <number metres ASL> | "-1000000" | sentinel string }
 * ```
 *
 * The `-1000000` sentinel signals "outside coverage"; the helper maps
 * it (and any non-finite value) to `null`.
 *
 * Cost
 * ----
 * Free, no auth, no documented per-IP rate limit. Per the EAS pack's
 * R2 risk: ~1000 req/day from a single IP is operationally fine; the
 * per-site Refresh-Evidence rate-limit at the calling layer already
 * caps user-driven hammering.
 *
 * Telemetry
 * ---------
 * Emits a `[provider:elevation]` breadcrumb (matching the bucket
 * vocabulary used in `lib/fetchPolygonElevations.ts` and
 * `services/dataProviders/siteProfile.ts`) so Cloud Run logs can grep
 * for the cascade's Stage-4 attempts.
 */

import type { LatLng } from '../../../lib/geometry/sampleGrid';
import { rethrowIfAborted, throwIfAborted } from '../../../lib/abortSignal';

/** Modern USGS EPQS v1 JSON endpoint. */
const USGS_EPQS_ENDPOINT = 'https://epqs.nationalmap.gov/v1/json';

/**
 * USGS sentinel value for "outside coverage". Real elevations
 * never approach this magnitude (Death Valley = -86 m is the
 * deepest land point in the US); any value at or below the
 * sentinel threshold is treated as null.
 */
const USGS_OUT_OF_COVERAGE_SENTINEL = -999999;

/**
 * Default timeout for a single EPQS request. Generous because EPQS
 * occasionally takes ~3-5 s under load; the polygon-elevation flow
 * runs at SED bootstrap, not in a tight UI loop, so 5 s is fine.
 */
const USGS_EPQS_TIMEOUT_MS = 5000;

/** Bucketed outcome for telemetry — mirrors the EL1 vocabulary. */
type UsgsEpqsBucket =
  | 'ok'
  | 'sentinel-out-of-coverage'
  | 'no-value'
  | 'parse-error'
  | 'non-200'
  | 'network-error'
  | 'invalid-coords';

/**
 * One sampled elevation point. Same shape used by the cascade
 * sampler so stages can be diff'd consistently.
 */
export interface UsgsEpqsSample {
  lat: number;
  lng: number;
  elevationMeters: number;
}

/**
 * Non-throwing coord-validity guard. Same predicate as
 * `lib/fetchPolygonElevations.ts` / `lib/refreshSiteElevation.ts` /
 * `services/dataProviders/types.ts` — duplicated inline to avoid
 * back-references from a focused leaf module.
 */
function isCoordValid(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

function logTelemetry(payload: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.info('[provider:elevation] response', {
    caller: 'usgsEpqsFetch',
    ...payload,
  });
}

/**
 * Fetch elevation for a single point via USGS EPQS. Returns metres
 * ASL on success, `null` on provider failure modes (invalid coords, HTTP
 * error, parse error, sentinel value, network error). An explicit caller abort
 * propagates so a superseded multi-stage analysis cannot continue.
 *
 * `fetchImpl` is parameterised so tests inject a deterministic mock —
 * **no real HTTPS calls in tests** (HARD RULE #8).
 *
 * @param point      `{ lat, lng }` WGS84 degrees.
 * @param fetchImpl  Optional fetch override (defaults to global `fetch`).
 * @param timeoutMs  Optional timeout override (defaults to 5000 ms).
 * @param signal     Attempt-owned cancellation signal.
 */
export async function fetchUsgsEpqs(
  point: LatLng,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = USGS_EPQS_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<UsgsEpqsSample | null> {
  throwIfAborted(signal);
  if (!isCoordValid(point.lat, point.lng)) {
    logTelemetry({
      bucket: 'invalid-coords' as UsgsEpqsBucket,
      lat: point.lat,
      lng: point.lng,
    });
    return null;
  }

  const controller = new AbortController();
  const abortFromCaller = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', abortFromCaller, { once: true });
  const timer = setTimeout(
    () => controller.abort(new Error(`USGS EPQS request timed out after ${timeoutMs}ms`)),
    timeoutMs,
  );

  try {
    const url =
      `${USGS_EPQS_ENDPOINT}` +
      `?x=${encodeURIComponent(String(point.lng))}` +
      `&y=${encodeURIComponent(String(point.lat))}` +
      `&units=Meters&wkid=4326&includeDate=false`;
    const res = await fetchImpl(url, { signal: controller.signal });
    throwIfAborted(signal);
    if (!res.ok) {
      logTelemetry({
        bucket: 'non-200' as UsgsEpqsBucket,
        httpStatus: res.status,
      });
      return null;
    }
    let data: { value?: number | string } = {};
    try {
      data = await res.json();
      throwIfAborted(signal);
    } catch (e) {
      rethrowIfAborted(signal, e);
      logTelemetry({
        bucket: 'parse-error' as UsgsEpqsBucket,
        error: (e as Error)?.message ?? 'unknown',
      });
      return null;
    }
    // EPQS returns `value` as either a number or a string (the
    // numeric format depends on the upstream's tile state). Coerce
    // defensively.
    const raw = data?.value;
    const v = typeof raw === 'string' ? Number(raw) : raw;
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      logTelemetry({
        bucket: 'no-value' as UsgsEpqsBucket,
        rawValue: raw ?? null,
      });
      return null;
    }
    if (v <= USGS_OUT_OF_COVERAGE_SENTINEL) {
      logTelemetry({
        bucket: 'sentinel-out-of-coverage' as UsgsEpqsBucket,
        sentinelValue: v,
      });
      return null;
    }
    logTelemetry({
      bucket: 'ok' as UsgsEpqsBucket,
      elevationMeters: v,
    });
    return { lat: point.lat, lng: point.lng, elevationMeters: v };
  } catch (e) {
    rethrowIfAborted(signal, e);
    // `AbortError` from the timeout lands here too — the cascade
    // sampler treats every failure mode the same way (try the next
    // stage / declare empty).
    logTelemetry({
      bucket: 'network-error' as UsgsEpqsBucket,
      error: (e as Error)?.message ?? 'unknown',
    });
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}
